import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from '@untitled-ui/icons-react';
import { type TFunctionNonStrict } from 'i18next';
import { useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '../../../../../components/buttons/button';
import {
  ProductTemplateEditor,
  type ProductTemplateEditorRef,
} from '../../../../../components/product-template-editor/product-template-editor';
import { TemplateCards } from '../../../../../components/product-template-editor/template-cards';
import { type ProductTemplate } from '../../../../../entity/api/product-template';
import { trackError } from '../../../../../error/tracking';
import { captureException } from '../../../../../logger/capture-exception';
import { prefetchProductTemplatesList } from '../../../../../services/storage/product-template-queries';
import { tGlobal, tNamespace } from '../../../../../services/translation';
import { FooterProductFunnel } from './-components/footer-product-funnel';

const t = tNamespace('app.funnel.product.$id._layout-funnel.index');

export const Route = createFileRoute('/app/funnel/product/$id/_layout-funnel/')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
  beforeLoad: function () {
    return Promise.allSettled([prefetchProductTemplatesList()]).catch(function (err) {
      captureException(
        err,
        {
          file: 'src/routes/app/funnel/product/$id/_layout-funnel.index.tsx',
          method: 'beforeLoad',
          infoMessage: 'unable to fetch product templates',
        },
        trackError
      );
    });
  },
});

function RouteComponent() {
  const [isCreatingNewTemplate, setIsCreatingNewTemplate] = useState(false);
  const [templateEditCandidate, setTemplateEditCandidate] = useState<ProductTemplate | null>(null);
  const navigate = useNavigate();
  const { id: productId }: { id: string } = Route.useParams();
  const templateEditor = useRef<ProductTemplateEditorRef>(null);

  function next() {
    navigate({
      to: `/app/funnel/product/${productId}/product-info`,
      params: { id: productId },
    }).catch(function (_err) {
      captureException(
        _err,
        {
          file: 'routes/app/funnel/product/$id/_layout-funnel.index.tsx',
          method: 'next',
          infoMessage: 'unable go to product info',
        },
        trackError
      );
    });
  }

  function cancel() {
    if (isCreatingNewTemplate) {
      setIsCreatingNewTemplate(false);
      return;
    }
    navigate({ to: '/app/products' }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/funnel/product/$id._layout-funnel.index.tsx',
          method: 'cancel',
          infoMessage: 'unable to navigate to /app/products',
        },
        trackError
      );
    });
  }

  return (
    <>
      <div className="h-full overflow-auto pb-10">
        {isCreatingNewTemplate ? (
          <>
            <ProductTemplateEditor
              onClose={function () {
                setTemplateEditCandidate(null);
                setIsCreatingNewTemplate(false);
              }}
              template={templateEditCandidate}
              ref={templateEditor}
              isNew={isCreatingNewTemplate}
            />
            <FooterProductFunnel
              cancel={
                <Button
                  variant="outline"
                  onClick={function () {
                    setIsCreatingNewTemplate(false);
                  }}
                >
                  {tGlobal('cancel')}
                </Button>
              }
              next={
                <Button
                  variant="accent"
                  onClick={function () {
                    templateEditor.current?.submit().catch(() => {});
                  }}
                >
                  {tGlobal('next')}
                </Button>
              }
            />
          </>
        ) : (
          <div className="pt-20">
            <h3 className="font-display-3 text-center">{t('title')}</h3>
            <p className="font-body-2-r mb-8 text-center text-branding-gray">
              <Trans
                t={t as unknown as TFunctionNonStrict}
                i18nKey="subTitle"
                components={[
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    key="link"
                    className="underline"
                  ></a>,
                ]}
              />
            </p>
            <Button
              variant="outline"
              onClick={function () {
                setIsCreatingNewTemplate(true);
              }}
              className="mx-auto mb-8"
            >
              {t('createNewTemplate')}
              <Plus width={16} height={16} />
            </Button>
            <TemplateCards
              onEdit={function (template) {
                setTemplateEditCandidate(template);
                setIsCreatingNewTemplate(true);
              }}
              onCreateNew={function () {
                setIsCreatingNewTemplate(true);
              }}
            />
            <FooterProductFunnel
              cancel={
                <Button variant="outline" onClick={cancel}>
                  {tGlobal('cancel')}
                </Button>
              }
              next={
                <Button variant="accent" onClick={next}>
                  {tGlobal('next')}
                </Button>
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
